import { timestampDate } from '@bufbuild/protobuf/wkt'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  type BiddingIntradayCsvRowResult,
  BiddingIntradayFormulaOperator,
  BiddingIntradayFormulaVariable,
  BiddingIntradayOrderOrderMethod,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import i18next from 'i18next'

import { convertAreaLabel } from '~/models/area'
import {
  BID_CSV_AREA_VALUE,
  BLOCK_BID_CSV_ISSUER_TYPE_VALUE,
  BLOCK_BID_CSV_SIDE_VALUE,
  type BidCsvLocalizedValue,
  INTRADAY_ORDER_CSV_HEADER_BY_LANG,
  SPOT_BID_CSV_ORDER_TYPE_VALUE,
  bidCsvLangOf,
} from '~/models/bidCsvHeader'
import type { CivilDate } from '~/models/civilDate'
import { formatJstTimeHm, jstYmd } from '~/models/dateTime'
import { formatKomaCompactLabel } from '~/models/komaLabel'
import type { IntradayCsvPreviewRow } from '~/pages/IntradayCsvImport/components/PreviewTable'

/** 売買方向の表示ラベル (画面共通の `common:tradeSide.*` を使う)。 */
const sideLabel = (side: Side): string => {
  if (side === Side.BUY) return i18next.t('common:tradeSide.buy')
  if (side === Side.SELL) return i18next.t('common:tradeSide.sell')
  return ''
}

const komaCell = (koma: number): string => {
  if (koma === 0) return ''
  return Number.isInteger(koma) && koma >= 1 && koma <= 48
    ? formatKomaCompactLabel(i18next.t.bind(i18next), koma)
    : String(koma)
}

/**
 * 発注主体 (LicenseType) → CSV の `発注主体区分` 列と同じ表示ラベル。
 *
 * OCCTO マスタ共通の [[licenseTypeLabel]] (発電 / 小売 / 自己託送) ではなく
 * **spec の発注主体区分の呼称** (需要 BG / 発電 / 自己託送 BG。 表示は conventions.md の
 * 略語スペースに従う) を出す: プレビューは
 * 「CSV に書いた値がどう解釈されたか」 を確認する場なので、 入力欄と同じ語彙で
 * 見せないと突き合わせられない (spec intraday-csv-import.md §2 / AC-BCI-004a)。
 * 需要抑制BG (DR) は時間前の発注主体区分に含まれない (issue #5052) ため未対応値。
 */
export const issuerCategoryLabel = (issuerType: LicenseType): string => {
  const t = i18next.t.bind(i18next)
  switch (issuerType) {
    case LicenseType.RETAIL:
      return t('page:intradayCsvImport.issuerCategory.demandBg')
    case LicenseType.GENERATION:
      return t('page:intradayCsvImport.issuerCategory.generation')
    case LicenseType.SELF_WHEELING:
      return t('page:intradayCsvImport.issuerCategory.selfWheelingBg')
    default:
      return ''
  }
}

/** 価格種別 (指値 / 成行) の表示ラベル。 CSV の区分値と同じ語彙。 */
const orderTypeLabel = (
  orderMethod: BiddingIntradayOrderOrderMethod,
): string => {
  if (orderMethod === BiddingIntradayOrderOrderMethod.LIMIT)
    return i18next.t('page:intradayCsvImport.orderType.limit')
  if (orderMethod === BiddingIntradayOrderOrderMethod.MARKET)
    return i18next.t('page:intradayCsvImport.orderType.market')
  return ''
}

/** 基準価格の表示ラベル。 CSV の区分値と同じ語彙 (spec §2 `基準価格` 列)。 */
const referencePriceLabel = (
  variable: BiddingIntradayFormulaVariable,
): string => {
  const t = i18next.t.bind(i18next)
  switch (variable) {
    case BiddingIntradayFormulaVariable.SPOT_EXECUTION:
      return t('page:intradayCsvImport.referencePrice.spotExecution')
    case BiddingIntradayFormulaVariable.INTRADAY_LAST:
      return t('page:intradayCsvImport.referencePrice.intradayLast')
    case BiddingIntradayFormulaVariable.INTRADAY_HIGH:
      return t('page:intradayCsvImport.referencePrice.intradayHigh')
    case BiddingIntradayFormulaVariable.INTRADAY_LOW:
      return t('page:intradayCsvImport.referencePrice.intradayLow')
    default:
      return ''
  }
}

/**
 * 演算子の表示記号。 CSV で `*` / `/` と書いた行も backend が × / ÷ に揃えて
 * 返すので、 表示は常に + / - / × / ÷ (spec §2 `演算子` 列)。
 */
/**
 * 基準価格エリアの表示ラベル。 CSV で空欄の行は backend も未設定で返すので空欄の
 * まま出し、 行のエリアに置き換えない (Step2 は CSV のまま見せる。 spec §7)。
 */
const referenceAreaLabel = (row: BiddingIntradayCsvRowResult): string => {
  switch (row.formulaReferenceArea.case) {
    case 'formulaReferenceAreaCode':
      return convertAreaLabel(row.formulaReferenceArea.value)
    case 'formulaReferenceSystemPrice':
      return row.formulaReferenceArea.value
        ? i18next.t('page:intradayCsvImport.referenceArea.systemPrice')
        : ''
    default:
      return ''
  }
}

const OPERATOR_SYMBOL: Record<BiddingIntradayFormulaOperator, string> = {
  [BiddingIntradayFormulaOperator.UNSPECIFIED]: '',
  [BiddingIntradayFormulaOperator.ADD]: '+',
  [BiddingIntradayFormulaOperator.SUBTRACT]: '-',
  [BiddingIntradayFormulaOperator.MULTIPLY]: '×',
  [BiddingIntradayFormulaOperator.DIVIDE]: '÷',
}

/**
 * 時間前入札のプレビュー行を表示用に整形する。
 * 値は backend が解決済みのものをそのまま表示する (frontend で再計算しない)。
 * 基準価格の行は 基準価格・基準価格エリア・演算子・調整値 を CSV の式のまま出し、 入札価格は
 * 空欄にする (確定した時点で計算するため Step2 では出さない。 spec §7)。
 *
 * 受渡日・自動取消時刻は保存時の形式 `YYYY/MM/DD` / `YYYY/MM/DD HH:MM` で、
 * 自動取消時刻は **JST 壁時計**として出す (= 画面の GC・コマ帯と同じ軸)。
 */
export const toIntradayPreviewRows = (
  rows: BiddingIntradayCsvRowResult[],
): IntradayCsvPreviewRow[] =>
  rows.map((row) => ({
    rowNumber: row.rowNumber,
    status: row.status,
    messages: row.messages,
    // 手数料自動考慮 ON の固定価格の行だけ設定される (AC-BIN-004b)。
    // 成行・基準価格の行、 OFF の行は undefined (PreviewTable が補足表示しない)。
    orderPriceYenPerKwh: row.orderPriceYenPerKwh?.value,
    columns: [
      formatIntradayCsvTargetDate(row.targetDate),
      komaCell(row.timeCode),
      issuerCategoryLabel(row.issuerType),
      convertAreaLabel(row.areaCode),
      sideLabel(row.direction),
      orderTypeLabel(row.orderMethod),
      row.priceYenPerKwh?.value ?? '',
      referencePriceLabel(row.referencePrice),
      referenceAreaLabel(row),
      OPERATOR_SYMBOL[row.formulaOperator],
      row.formulaValue?.value ?? '',
      row.volumeMw?.value ?? '',
      formatWithdrawAt(row),
      row.memoJepx ?? '',
      row.memoFree ?? '',
    ],
  }))

const pad2 = (n: number): string => String(n).padStart(2, '0')

/**
 * 受渡日を保存時の形式 `YYYY/MM/DD` で出す (spec §2 列 1)。 差分プレビューの行と、
 * 受渡日ごとの件数 (AC-BCI-002) が同じ書式になるよう、 両方がこれを使う。
 */
export const formatIntradayCsvTargetDate = (
  date: CivilDate | undefined,
): string => {
  if (date === undefined) return ''
  const { year, month, day } = date
  return `${year}/${pad2(month)}/${pad2(day)}`
}

const formatWithdrawAt = (row: BiddingIntradayCsvRowResult): string => {
  if (row.withdrawAt === undefined) return ''
  const d = timestampDate(row.withdrawAt)
  const { year, month, day } = jstYmd(d)
  return `${year}/${pad2(month)}/${pad2(day)} ${formatJstTimeHm(d)}`
}

/**
 * 雛形の記入例データ (spec intraday-csv-import.md §2「雛形の記入例データ」の
 * 2 パターン: 指値 / 成行。 基準価格・基準価格エリア・演算子・調整値は空欄にする: 値がある行は
 * 取込で行エラーになる〈AC-BCI-041〉)。 列順は `INTRADAY_ORDER_CSV_HEADER` と同じ。
 * 受渡日はダミー値で、 受付開始より前の受渡日なら取込で行エラーになる (同節の注記)。
 *
 * 表示言語で変わる値 (区分値・エリア・ダミーの文) は日本語と英語の組で持つ
 * (spec §2「英語表示対応」、 ダミーの文の英語は同 spec の英語付録)。 受渡日・コマ・
 * 価格・数量・時刻は言語で変わらないので文字列のまま。 英語の区分値は
 * 取込が受理する (backend `intraday_order_csv.go`、 AC-SPS-031)。
 */
export const INTRADAY_CSV_TEMPLATE_SAMPLE_DELIVERY_DATE = '2026/09/01'

const INTRADAY_CSV_TEMPLATE_SAMPLE_CELLS: Array<
  Array<string | BidCsvLocalizedValue>
> = [
  [
    INTRADAY_CSV_TEMPLATE_SAMPLE_DELIVERY_DATE,
    '17',
    BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    BID_CSV_AREA_VALUE.tokyo,
    BLOCK_BID_CSV_SIDE_VALUE.buy,
    SPOT_BID_CSV_ORDER_TYPE_VALUE.limit,
    '10.55',
    '',
    '',
    '',
    '',
    '10.5',
    '2026/09/01 06:30',
    { ja: '東京需要分', en: 'For Tokyo demand' },
    { ja: '需要上振れ分を補填', en: 'Cover demand above forecast' },
  ],
  [
    INTRADAY_CSV_TEMPLATE_SAMPLE_DELIVERY_DATE,
    '18',
    BLOCK_BID_CSV_ISSUER_TYPE_VALUE.generation,
    BID_CSV_AREA_VALUE.kansai,
    BLOCK_BID_CSV_SIDE_VALUE.sell,
    SPOT_BID_CSV_ORDER_TYPE_VALUE.market,
    '',
    '',
    '',
    '',
    '',
    '5.0',
    '',
    '',
    '',
  ],
]

/**
 * 時間前入札 CSV の雛形本文 (Step1「テンプレートを ダウンロード」、
 * spec intraday-csv-import.md §2 / common.md AC-DUC-031・§6)。 表示言語のヘッダー行の
 * あとに記入例行を置く。 英語表示ではヘッダー行を英語ヘッダー名、 区分値を英語の値で出す
 * (common.md §6)。 ヘッダーは自画面の DropZone と同じ `INTRADAY_ORDER_CSV_HEADER_BY_LANG`
 * から引き、 DL した雛形が自画面の厳格ヘッダーチェックを通ることを保つ。 記入例の値は
 * カンマ・引用符を含まないので引用しない。
 */
export function buildIntradayCsvTemplate(isEnglish: boolean): string {
  const lang = bidCsvLangOf(isEnglish)
  const rows = [
    INTRADAY_ORDER_CSV_HEADER_BY_LANG[lang],
    ...INTRADAY_CSV_TEMPLATE_SAMPLE_CELLS.map((cells) =>
      cells.map((cell) => (typeof cell === 'string' ? cell : cell[lang])),
    ),
  ]
  return rows.map((cells) => `${cells.join(',')}\n`).join('')
}

/**
 * 雛形 CSV のファイル名 (common.md §6 のデータ種別名、 #9332)。 表示言語で切り替える。
 */
const INTRADAY_CSV_TEMPLATE_FILE_NAMES = {
  ja: '時間前入札_テンプレート.csv',
  en: 'intraday-bid_template.csv',
}

export function intradayCsvTemplateFileName(isEnglish: boolean): string {
  return isEnglish
    ? INTRADAY_CSV_TEMPLATE_FILE_NAMES.en
    : INTRADAY_CSV_TEMPLATE_FILE_NAMES.ja
}
